import { ArrowDownSmallIcon } from '@blocksuite/icons/rc';
import { useEditCollectionName } from '@pique/core/components/page-list';
import { createEmptyCollection } from '@pique/core/components/page-list/use-collection-manager';
import { MenuItem } from '@pique/core/components/app-sidebar';
import { CollectionService } from '@pique/core/modules/collection';
import { ExplorerTreeRoot } from '@pique/core/modules/explorer/views/tree';
import { WorkbenchService } from '@pique/core/modules/workbench';
import { useI18n } from '@pique/i18n';
import { useLiveData, useServices } from '@toeverything/infra';
import { Folder02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { nanoid } from 'nanoid';
import { useCallback } from 'react';

import { ExplorerService } from '../../../services/explorer';
import { ExplorerCollectionNode } from '../../nodes/collection';
import { RootEmpty } from './empty';

export const ExplorerCollections = () => {
  const t = useI18n();
  const { collectionService, workbenchService, explorerService } = useServices({
    CollectionService,
    WorkbenchService,
    ExplorerService,
  });
  const explorerSection = explorerService.sections.collections;
  const collections = useLiveData(collectionService.collections$);
  const collapsed = useLiveData(explorerSection.collapsed$);
  const { node, open: openCreateCollectionModel } = useEditCollectionName({
    title: t['com.pique.editCollection.createCollection'](),
    showTips: true,
  });

  const handleCreateCollection = useCallback(() => {
    openCreateCollectionModel('')
      .then(name => {
        const id = nanoid();
        collectionService.addCollection(createEmptyCollection(id, { name }));
        workbenchService.workbench.openCollection(id);
        explorerSection.setCollapsed(false);
      })
      .catch(err => {
        console.error(err);
      });
  }, [
    collectionService,
    explorerSection,
    openCreateCollectionModel,
    workbenchService.workbench,
  ]);

  const handleCollapsedChange = useCallback(
    (v: boolean) => explorerSection.setCollapsed(v),
    [explorerSection]
  );

  return (
    <>
      <MenuItem
        data-testid="explorer-collections"
        icon={<HugeiconsIcon icon={Folder02Icon} size={18} strokeWidth={1.5} />}
        collapsed={collapsed}
        onCollapsedChange={handleCollapsedChange}
        postfix={
          <ArrowDownSmallIcon
            style={{
              transform: collapsed ? 'rotate(-90deg)' : undefined,
              transition: 'transform 0.2s',
              fontSize: 16,
            }}
          />
        }
      >
        <span>{t['com.pique.rootAppSidebar.collections']()}</span>
      </MenuItem>
      {!collapsed && (
        <ExplorerTreeRoot
          placeholder={<RootEmpty onClickCreate={handleCreateCollection} />}
        >
          {collections.map(collection => (
            <ExplorerCollectionNode
              key={collection.id}
              collectionId={collection.id}
              reorderable={false}
              location={{
                at: 'explorer:collection:list',
              }}
            />
          ))}
        </ExplorerTreeRoot>
      )}
      {node}
    </>
  );
};
